import {
  formatEnrollmentActivityActor,
  parseEnrollmentTimestamp,
} from "@/features/user-tasks/utils/enrollment-activity-display";
import { formatDateTableDisplay } from "@/lib/utils/date/date";
import { formatStudioInstant } from "@/lib/utils/date/format-studio-instant";
import { getScheduledDropDateYmd } from "@/lib/utils/enrollment";
import { getEnrollmentStatusDisplayLabel } from "@/lib/utils/enrollment-status-display";
import type {
  ClassEnrollmentActivity,
  GetEnrollmentResponse,
} from "@/openapi/generated";
import { EnrollmentStatus } from "@/openapi/generated";
import { formatClassEnrollmentStudentName } from "./classSingleEnrollment";

export type EnrollmentHistorySubject = "student" | "class";

/** Modal copy keyed by row subject: class page → student rows; student page → class rows. */
export function enrollmentHistoryModalDescription(
  rowSubject: EnrollmentHistorySubject,
): string {
  return rowSubject === "student"
    ? "Every enroll and drop for this class, including students who are no longer on the roster. Each row shows the date, time, and the user who made the change."
    : "Every enroll and drop for this student, including classes they have already left. Each row shows the date, time, and the user who made the change.";
}

export function enrollmentHistoryModalEmptyMessage(
  rowSubject: EnrollmentHistorySubject,
): string {
  return rowSubject === "student"
    ? "No one has been enrolled in this class yet."
    : "This student has no enrollment history yet.";
}

export type ClassEnrollmentHistoryEvent = {
  id: string;
  enrollmentId: number | null;
  subjectLabel: string;
  subjectId: number | null;
  actionLabel: string;
  /** Shown in the modal (may be a future effective date, e.g. scheduled withdraw). */
  occurredAt: Date | null;
  /** Newest-first ordering — uses when the change was recorded, not a future effective date. */
  sortAt: Date | null;
  actorLabel: string;
};

function formatActivityAction(action: string | undefined): string {
  if (!action?.trim()) return "Status updated";
  return action
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

function withdrawalEffectiveYmd(
  activity: ClassEnrollmentActivity,
  enrollment: GetEnrollmentResponse,
): string | null {
  const fromEnrollment = getScheduledDropDateYmd(enrollment);
  if (fromEnrollment) return fromEnrollment;

  if (isDateOnlyIsoDate(enrollment.dropDate)) {
    return String(enrollment.dropDate).trim();
  }
  if (isDateOnlyIsoDate(enrollment.droppedAt)) {
    return String(enrollment.droppedAt).trim();
  }

  const occurred = parseEnrollmentTimestamp(activity.occurredAt);
  if (occurred && isUtcMidnightInstant(occurred)) {
    const y = occurred.getUTCFullYear();
    const m = String(occurred.getUTCMonth() + 1).padStart(2, "0");
    const d = String(occurred.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return null;
}

function formatActivityLabel(
  activity: ClassEnrollmentActivity,
  enrollment: GetEnrollmentResponse,
): string {
  const from = activity.fromStatus
    ? getEnrollmentStatusDisplayLabel(activity.fromStatus)
    : "";
  const to = activity.toStatus
    ? getEnrollmentStatusDisplayLabel(activity.toStatus)
    : "";

  if (isWithdrawActivity(activity)) {
    const effectiveYmd = withdrawalEffectiveYmd(activity, enrollment);
    if (effectiveYmd) {
      const fromLabel = from || "Enrolled";
      return `${fromLabel} → Withdrawal on date (${formatDateTableDisplay(effectiveYmd)})`;
    }
  }

  if (from && to && from !== to) return `${from} → ${to}`;
  if (to) return to;
  if (from) return from;
  return formatActivityAction(activity.action);
}

function activityDisplayTime(activity: ClassEnrollmentActivity): Date | null {
  return (
    parseEnrollmentTimestamp(activity.occurredAt) ??
    parseEnrollmentTimestamp(activity.createdAt)
  );
}

function isUtcMidnightInstant(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

function isWithdrawActivity(activity: ClassEnrollmentActivity): boolean {
  const action = activity.action?.trim().toLowerCase() ?? "";
  const toStatus = String(activity.toStatus ?? "");
  return (
    toStatus === EnrollmentStatus.Dropped ||
    toStatus === EnrollmentStatus.ScheduledDrop ||
    action.includes("drop") ||
    action.includes("withdraw")
  );
}

/** API often sends `2026-10-01` for withdraw effective date — not when staff acted. */
function isDateOnlyIsoDate(raw: unknown): boolean {
  return typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim());
}

function enrollmentRecordedAt(
  enrollment: GetEnrollmentResponse,
): Date | null {
  const updated = parseEnrollmentTimestamp(enrollment.updatedAt);
  if (updated) return updated;

  if (isDateOnlyIsoDate(enrollment.statusChangedAt)) {
    return null;
  }

  return parseEnrollmentTimestamp(enrollment.statusChangedAt);
}

/**
 * When `occurredAt` is the withdraw effective date (often UTC midnight), use when
 * the change was recorded on the activity or parent enrollment.
 */
function resolveActivityRecordedAt(
  activity: ClassEnrollmentActivity,
  enrollment: GetEnrollmentResponse,
  now: Date = new Date(),
): Date | null {
  const occurred = parseEnrollmentTimestamp(activity.occurredAt);
  const activityCreated = parseEnrollmentTimestamp(activity.createdAt);
  const activityUpdated = parseEnrollmentTimestamp(activity.updatedAt);
  const enrollmentRecorded = enrollmentRecordedAt(enrollment);

  if (isWithdrawActivity(activity) && occurred && isUtcMidnightInstant(occurred)) {
    const candidates = [activityCreated, activityUpdated, enrollmentRecorded].filter(
      (value): value is Date => value != null,
    );
    const beforeEffective = candidates.find(
      (candidate) => candidate.getTime() < occurred.getTime(),
    );
    if (beforeEffective) return beforeEffective;

    if (occurred.getTime() > now.getTime()) {
      return enrollmentRecorded ?? activityCreated ?? activityUpdated ?? null;
    }

    if (enrollmentRecorded) return enrollmentRecorded;
  }

  return (
    activitySortTime(activity, now) ??
    activityCreated ??
    activityUpdated ??
    enrollmentRecorded ??
    occurred
  );
}

/** Future `occurredAt` is often the withdraw effective date; sort by when it was set. */
export function activitySortTime(
  activity: ClassEnrollmentActivity,
  now: Date = new Date(),
): Date | null {
  const occurred = parseEnrollmentTimestamp(activity.occurredAt);
  const created = parseEnrollmentTimestamp(activity.createdAt);
  const updated = parseEnrollmentTimestamp(activity.updatedAt);

  if (occurred && occurred.getTime() > now.getTime()) {
    return created ?? updated ?? occurred;
  }

  if (
    occurred &&
    isUtcMidnightInstant(occurred) &&
    isWithdrawActivity(activity)
  ) {
    const beforeEffective =
      (created && created.getTime() < occurred.getTime() ? created : null) ??
      (updated && updated.getTime() < occurred.getTime() ? updated : null);
    if (beforeEffective) return beforeEffective;
  }

  return occurred ?? created ?? updated;
}

function dropEventTimes(enrollment: GetEnrollmentResponse): {
  displayAt: Date | null;
  sortAt: Date | null;
} {
  const droppedAt = parseEnrollmentTimestamp(enrollment.droppedAt);
  const statusChangedAt = isDateOnlyIsoDate(enrollment.statusChangedAt)
    ? null
    : parseEnrollmentTimestamp(enrollment.statusChangedAt);
  const updatedAt = parseEnrollmentTimestamp(enrollment.updatedAt);
  const now = new Date();

  if (droppedAt && droppedAt.getTime() > now.getTime()) {
    return {
      displayAt: droppedAt,
      sortAt: updatedAt ?? statusChangedAt ?? droppedAt,
    };
  }

  return {
    displayAt: droppedAt ?? statusChangedAt,
    sortAt: updatedAt ?? statusChangedAt ?? droppedAt,
  };
}

function positiveId(value: unknown): number | null {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function subjectOf(
  enrollment: GetEnrollmentResponse,
  subject: EnrollmentHistorySubject,
): { label: string; id: number | null } {
  if (subject === "class") {
    const classId = positiveId(enrollment._class?.classId);
    const name = enrollment._class?.name?.trim();
    return {
      label: name || (classId != null ? `Class #${classId}` : "Class"),
      id: classId,
    };
  }

  return {
    label: formatClassEnrollmentStudentName(enrollment),
    id: positiveId(enrollment.student?.studentId),
  };
}

function enrollmentIdOf(enrollment: GetEnrollmentResponse): number | null {
  return positiveId(enrollment.enrollmentId);
}

function fallbackEvents(
  enrollment: GetEnrollmentResponse,
  subject: { label: string; id: number | null },
): ClassEnrollmentHistoryEvent[] {
  const enrollmentId = enrollmentIdOf(enrollment);
  const enrollmentKey = String(enrollmentId ?? subject.label);
  const events: ClassEnrollmentHistoryEvent[] = [];
  const enrolledAt = parseEnrollmentTimestamp(enrollment.enrolledAt);
  if (enrolledAt) {
    events.push({
      id: `${enrollmentKey}-enrolled`,
      enrollmentId,
      subjectLabel: subject.label,
      subjectId: subject.id,
      actionLabel: "Enrolled",
      occurredAt: enrolledAt,
      sortAt: enrolledAt,
      actorLabel: "Unknown user",
    });
  }

  const status = enrollment.status;
  const dropTimes = dropEventTimes(enrollment);
  if (status === EnrollmentStatus.Dropped || dropTimes.displayAt) {
    events.push({
      id: `${enrollmentKey}-dropped`,
      enrollmentId,
      subjectLabel: subject.label,
      subjectId: subject.id,
      actionLabel: "Dropped",
      occurredAt: dropTimes.displayAt,
      sortAt: dropTimes.sortAt,
      actorLabel: "Unknown user",
    });
  }

  return events;
}

export function buildClassEnrollmentHistoryEvents(
  enrollments: readonly GetEnrollmentResponse[],
  subject: EnrollmentHistorySubject = "student",
): ClassEnrollmentHistoryEvent[] {
  const events: ClassEnrollmentHistoryEvent[] = [];

  for (const enrollment of enrollments) {
    const rowSubject = subjectOf(enrollment, subject);
    const enrollmentId = enrollmentIdOf(enrollment);
    const enrollmentKey = String(enrollmentId ?? rowSubject.label);
    const history = enrollment.activityHistory ?? [];

    if (history.length === 0) {
      events.push(...fallbackEvents(enrollment, rowSubject));
      continue;
    }

    history.forEach((activity, index) => {
      const displayAt = activityDisplayTime(activity);
      const sortAt = resolveActivityRecordedAt(activity, enrollment);
      events.push({
        id: `${enrollmentKey}-${String(activity.id ?? index)}-${sortAt?.getTime() ?? index}`,
        enrollmentId,
        subjectLabel: rowSubject.label,
        subjectId: rowSubject.id,
        actionLabel: formatActivityLabel(activity, enrollment),
        occurredAt: displayAt,
        sortAt,
        actorLabel: formatEnrollmentActivityActor(activity),
      });
    });
  }

  return events.sort((a, b) => {
    const aTime = a.sortAt?.getTime() ?? 0;
    const bTime = b.sortAt?.getTime() ?? 0;
    if (aTime !== bTime) return bTime - aTime;
    return a.subjectLabel.localeCompare(b.subjectLabel);
  });
}

/** When the staff action was recorded (withdrawal effective date lives in the action label). */
export function formatEnrollmentHistoryWhenLabel(
  event: Pick<
    ClassEnrollmentHistoryEvent,
    "occurredAt" | "sortAt" | "actionLabel"
  >,
): string {
  const recordedAt = event.sortAt ?? event.occurredAt;
  if (!recordedAt) return "—";

  const whenLabel = formatStudioInstant(recordedAt);
  if (event.actionLabel.includes("Withdrawal on date")) {
    return whenLabel;
  }

  const effectiveAt = event.occurredAt;
  if (
    effectiveAt &&
    event.sortAt &&
    effectiveAt.getTime() !== event.sortAt.getTime()
  ) {
    return `${whenLabel} · effective ${formatStudioInstant(effectiveAt)}`;
  }

  return whenLabel;
}
